/** @prettier */
import React, { Component, useState, useEffect } from "react";
import "./QuestionEditor.css";
import { Button, ControlLabel, FormGroup, Label } from "react-bootstrap";
import QuestionTabs from "./QuestionTabs.js";
import Comments from "./Comments.js";
import { QuestionAnswersView as QuestionAnswers } from "./QuestionAnswers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    withRouter,
    RouteComponentProps,
    useParams,
    useHistory,
} from "react-router";
import { getStatusBsStyle, getStatusDisplayString } from "../util/statusUtils";
import * as questionApi from "./questionApi";
import { toDataUri, partitionImages } from "../util/imageUtils";
import qBankApi, { User, Subscription } from "../services/qbankApi";
import { assert } from "../util/assert";
import {
    QuestionViewerModalState,
    QuestionViewerModals,
} from "./QuestionViewerModals";

type ImageViewProps = {
    imageFile: questionApi.QuestionImageData;
};
type ImageViewState = { imageData: string };
class Image extends Component<ImageViewProps, ImageViewState> {
    state: ImageViewState = {
        imageData: "#",
    };

    componentDidMount() {
        const image = this.props.imageFile.image;
        if (typeof image === "string") {
            if (image) {
                // TODO: Get filetype from API once available
                const fileName = this.props.imageFile.fileName;
                this.setState({ imageData: toDataUri(image, fileName) });
            }
        } else {
            const imageReader = new FileReader();

            imageReader.onload = (e) => {
                const imageData = e.target?.result;
                if (typeof imageData === "string") {
                    this.setState({ imageData });
                }
            };
            imageReader.readAsDataURL(image);
        }
    }

    render() {
        if (this.state.imageData !== "#") {
            return (
                <img
                    className="question-viewer-image"
                    src={this.state.imageData}
                    alt="Figure"
                />
            );
        } else {
            return <></>;
        }
    }
}

type ImageGroupProps = {
    imageFiles: questionApi.QuestionImageData[];
};
const ImageGroup = ({ imageFiles }: ImageGroupProps) => {
    return (
        <div className="question-viewer-image-group">
            {imageFiles.map((imageFile, index) => (
                <Image key={index} imageFile={imageFile} />
            ))}
        </div>
    );
};

const editableStatuses = new Set([
    "DRAFT",
    "SUBMITTED",
    "NEEDS_REVISION",
    "PUBLISHED",
]);

type ReferencesProps = {
    references: string[];
};
const References = ({ references }: ReferencesProps) => (
    <FormGroup className="question-references" controlId="question-references">
        <ControlLabel>Reference(s)</ControlLabel>
        <ul>
            {references.map((value, index) => {
                return <li key={index}>{value}</li>;
            })}
        </ul>
    </FormGroup>
);

const KeyConcept = ({ keyConcept }: { keyConcept: string }) => (
    <FormGroup className="question-key-points" controlId="question-key-points">
        <ControlLabel>Key concept</ControlLabel>
        <div>{keyConcept}</div>
    </FormGroup>
);

type ReinforcementQuestionsProps = {
    reinforcementQuestions: questionApi.ReinforcementQuestion[];
};
const ReinforcementQuestions = ({
    reinforcementQuestions,
}: ReinforcementQuestionsProps) => (
    <FormGroup
        className="question-reinforcement-questions"
        controlId="question-reinforcement-questions"
    >
        {reinforcementQuestions.map((value) => {
            return (
                <div key={value.question}>
                    <ControlLabel>Reinforcement question</ControlLabel>
                    <div>{value.question}</div>
                    <ControlLabel className="reinforcement-answer-view">
                        Reinforcement answer
                    </ControlLabel>
                    <div>{value.answer}</div>
                </div>
            );
        })}
    </FormGroup>
);

type QuestionExplanationProps = { explanation: string; label: string };
const QuestionExplanation = ({
    explanation,
    label,
}: QuestionExplanationProps) => (
    <FormGroup controlId="question-explanation">
        <ControlLabel>{label}</ControlLabel>
        <div>{explanation}</div>
    </FormGroup>
);

type QuestionStemProps = {
    stem: string;
    questionStemImages: questionApi.QuestionImageData[];
};
const QuestionStem = ({ stem, questionStemImages }: QuestionStemProps) => (
    <>
        <FormGroup className="question-text" controlId="question-text">
            <ControlLabel>Question stem</ControlLabel>
            <div>{stem}</div>
        </FormGroup>
        <ImageGroup key="stem" imageFiles={questionStemImages} />
    </>
);

type LearningObjectiveProps = {
    learningObjective: string;
};
const LearningObjective = ({ learningObjective }: LearningObjectiveProps) => (
    <div>
        <FormGroup controlId="question-learning-objective">
            <ControlLabel>Learning objective</ControlLabel>
            <div>{learningObjective}</div>
        </FormGroup>
    </div>
);

type QuestionInfoProps = {
    question: questionApi.DraftQuestion;
};
const QuestionInfo = ({ question }: QuestionInfoProps) => (
    <div className="question-info">
        <Label
            className="question-status-label"
            bsStyle={getStatusBsStyle(question.status)}
        >
            {getStatusDisplayString(question.status)}
        </Label>
        <Label className="question-subject-label">
            {/* We shouldn't preview without these but probably not worth expressing in the type */}
            <span>{question.subject?.name}</span>
            <FontAwesomeIcon icon="chevron-right" />
        </Label>
        <Label className="question-category-label">
            <span>{question.category?.name}</span>
            <FontAwesomeIcon icon="chevron-right" />
        </Label>
        <Label className="question-subcategory-label">
            <span>{question.subcategory?.name}</span>
        </Label>
    </div>
);

type MouseEvent = React.MouseEvent<Button>;

type QuestionStatus = questionApi.Question["status"];
type AllowedStatus = { status: QuestionStatus };

type QuestionViewWrapperProps = {
    me: User;
    activeSubscription: Subscription;
};
type QuestionViewerProps = QuestionViewWrapperProps &
    RouteComponentProps<{}> & {
        question: QuestionViewerQuestionData;
        isPreview: boolean;
        hasChanges: boolean;
        onEdit: () => void;
    };
export type QuestionViewerQuestionData = questionApi.DraftQuestion & {
    questionStemImages: questionApi.QuestionImageData[];
    questionExplanationImages: questionApi.QuestionImageData[];
    removedImages: questionApi.RemovedImageData[];
};
type QuestionViewerState = {
    showUpdateError: boolean;
    showingModal?: QuestionViewerModalState;
    commentList: questionApi.Comment[];
    allowedStatuses: AllowedStatus[];
};
class _QuestionViewer extends Component<
    QuestionViewerProps,
    QuestionViewerState
> {
    state: QuestionViewerState = {
        showUpdateError: false,
        commentList: [],
        allowedStatuses: [],
    };
    constructor(props: QuestionViewerProps) {
        super(props);
        this.addComment = this.addComment.bind(this);
        this.submitUpdateQuestionStatus = this.submitUpdateQuestionStatus.bind(
            this,
        );
        this.handleSubmitClick = this.handleSubmitClick.bind(this);
        this.handleConfirmDeleteClick = this.handleConfirmDeleteClick.bind(
            this,
        );
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handlePublishClick = this.handlePublishClick.bind(this);
        this.handleNeedsRevisionClick = this.handleNeedsRevisionClick.bind(
            this,
        );
        this.handleSaveAsDraftClick = this.handleSaveAsDraftClick.bind(this);
        this.handleConfirmSaveAsDraftClick = this.handleConfirmSaveAsDraftClick.bind(
            this,
        );
        this.discardChanges = this.discardChanges.bind(this);
    }
    componentDidMount() {
        this.setState({ commentList: this.props.question.commentList });

        let allowedStatusesPromise: Promise<AllowedStatus[]>;
        const questionId = this.props.question.uuid;
        if (!questionId) {
            allowedStatusesPromise = Promise.resolve([
                { status: "DRAFT" },
                { status: "SUBMITTED" },
            ]);
        } else {
            allowedStatusesPromise = qBankApi.getQuestionAllowedStatuses(
                questionId,
            );
        }

        allowedStatusesPromise.then((allowedStatuses) => {
            this.setState({ allowedStatuses });
        });
    }
    addComment(comment: questionApi.Comment) {
        this.setState({
            commentList: this.state.commentList.concat([comment]),
        });
    }
    submitUpdateQuestionStatus(newStatus: QuestionStatus) {
        const questionClone = {
            ...this.props.question,
            status: newStatus,
        };

        const {
            uuid,
            questionStemImages,
            questionExplanationImages,
            removedImages,
        } = this.props.question;

        return questionApi
            .saveQuestion(uuid, questionClone, {
                removed: removedImages,
                stem: questionStemImages,
                explanation: questionExplanationImages,
            })
            .then((uuid) => ({
                // add in the uuid, which might have been assigned by the backend
                // for a newly created question
                ...questionClone,
                uuid,
            }));
    }
    handleConfirmSaveAsDraftClick() {
        this.setState({ showingModal: "saveAsNewVersionDraft" });
    }
    handleSaveAsDraftClick() {
        this.handleSaveClick(true);
    }
    async handleSaveClick(saveAsDraft?: boolean) {
        const {
            questionStemImages,
            questionExplanationImages,
            removedImages,
            ...question
        } = this.props.question;

        if (saveAsDraft) {
            question.status = "DRAFT";
        }

        await questionApi.saveQuestion(question.uuid, question, {
            removed: removedImages,
            stem: questionStemImages,
            explanation: questionExplanationImages,
        });

        this.props.history.push("/questions");
    }
    handleSubmitClick() {
        this.submitUpdateQuestionStatus("SUBMITTED")
            .then((question) => {
                this.props.history.replace(
                    "/questions/" + question.uuid + "/view",
                );
                window.location.reload();
            })
            .catch((error) => {
                console.log("Error submitting question:", error);
                this.setState({ showUpdateError: true });
            });
    }
    handleConfirmDeleteClick(event: MouseEvent) {
        this.setState({ showingModal: "confirmDelete" });
        event.preventDefault();
    }
    handleDeleteClick() {
        questionApi.deleteQuestion(this.props.question.uuid!).then(() => {
            this.props.history.push("/questions");
        });
    }
    handlePublishClick() {
        this.submitUpdateQuestionStatus("PUBLISHED")
            .then((question) => {
                this.props.history.replace(
                    "/questions/" + question.uuid + "/view",
                );
                window.location.reload();
            })
            .catch(() => {
                this.setState({ showUpdateError: true });
            });
    }
    handleNeedsRevisionClick(event: MouseEvent) {
        this.submitUpdateQuestionStatus("NEEDS_REVISION")
            .then((question) => {
                this.props.history.replace(
                    "/questions/" + question.uuid + "/view",
                );
                window.location.reload();
            })
            .catch(() => {
                this.setState({ showUpdateError: true });
            });
        event.preventDefault();
    }
    discardChanges() {
        // TODO: improve
        this.props.history.goBack();
    }
    render() {
        const question = this.props.question;
        const isNew = !question.uuid;

        const currentState = question.status.toUpperCase();
        const isNextStateAllowed = (nextState: QuestionStatus) => {
            if (nextState === currentState && currentState !== "PUBLISHED") {
                return false;
            }
            // TODO remove if fixed by API
            if (nextState === "NEEDS_REVISION" && currentState === "DRAFT") {
                return false;
            }

            return !!this.state.allowedStatuses.find(
                (s) => s.status === nextState,
            );
        };

        const { versionNum, latestVersionNum } = question.versionInfo ?? {
            versionNum: 1,
            latestVersionNum: 1,
        };
        const hasPreviousVersion = versionNum > 1;
        const isLatestVersion = versionNum === latestVersionNum;

        const isEditableStatus = editableStatuses.has(question.status);
        const canEdit = isLatestVersion && isEditableStatus;
        const canSave = isEditableStatus && this.props.isPreview;
        const isAuthorOrAuditor =
            this.props.activeSubscription.questionAuditor ||
            question.author?.uuid === this.props.me.uuid;

        const questionView = (
            <form autoComplete="off" className="question-form">
                <div>
                    <LearningObjective
                        learningObjective={question.learningObjective}
                    />
                    <QuestionStem
                        stem={question.stem}
                        questionStemImages={question.questionStemImages}
                    />
                    <QuestionAnswers answerChoices={question.answerChoices} />
                    <KeyConcept keyConcept={question.keyConcept} />
                    <QuestionExplanation
                        label="Correct answer explanation"
                        explanation={question.correctAnswerExplanation}
                    />
                    <QuestionExplanation
                        label="Incorrect answers explanation"
                        explanation={question.incorrectAnswerExplanation}
                    />
                    <ImageGroup
                        key="explanation"
                        imageFiles={question.questionExplanationImages}
                    />
                    <ReinforcementQuestions
                        reinforcementQuestions={question.reinforcementQuestions}
                    />
                    <References references={question.references} />
                </div>
                {this.state.showUpdateError && (
                    <div className="update-error">
                        There was an error updating the question. Edit the
                        question to make sure it is valid, then try again.
                    </div>
                )}
                <div className="question-action-buttons">
                    {canEdit && (
                        <Button
                            bsStyle="default"
                            onClick={() => this.props.onEdit()}
                        >
                            Edit
                        </Button>
                    )}
                    {canSave &&
                        (currentState === "PUBLISHED" ? (
                            <Button
                                bsStyle={getStatusBsStyle("DRAFT")}
                                onClick={this.handleConfirmSaveAsDraftClick}
                            >
                                Save as new version Draft
                            </Button>
                        ) : (
                            <Button
                                disabled={!this.props.hasChanges}
                                title={
                                    !this.props.hasChanges
                                        ? "No changes to save"
                                        : ""
                                }
                                bsStyle={getStatusBsStyle("DRAFT")}
                                onClick={() => {
                                    if (currentState === "SUBMITTED") {
                                        this.setState({
                                            showingModal:
                                                "saveSubmittedAsDraft",
                                        });
                                    } else {
                                        this.handleSaveAsDraftClick();
                                    }
                                }}
                            >
                                Save as Draft
                            </Button>
                        ))}
                    {(this.props.isPreview ||
                        currentState === "NEEDS_REVISION") &&
                        isNextStateAllowed("SUBMITTED") && (
                            <Button
                                bsStyle="primary"
                                onClick={(event) => {
                                    event.preventDefault();
                                    if (currentState === "PUBLISHED") {
                                        this.setState({
                                            showingModal:
                                                "confirmSubmitNewVersion",
                                        });
                                    } else {
                                        this.handleSubmitClick();
                                    }
                                }}
                            >
                                {currentState === "PUBLISHED"
                                    ? "Submit new version"
                                    : "Submit"}
                            </Button>
                        )}
                    {this.props.isPreview && question.status === "SUBMITTED" && (
                        <Button
                            bsStyle="info"
                            onClick={() => this.handleSaveClick()}
                        >
                            Submit Changes
                        </Button>
                    )}
                    {this.props.activeSubscription.questionAuditor &&
                        /* Can only publish drafts and re-publish published questions after editing */
                        (this.props.isPreview ||
                            question.status === "SUBMITTED" ||
                            question.status === "NEEDS_REVISION") &&
                        isNextStateAllowed("PUBLISHED") && (
                            <Button
                                bsStyle={getStatusBsStyle("PUBLISHED")}
                                onClick={(event) => {
                                    event.preventDefault();
                                    if (
                                        currentState === "PUBLISHED" ||
                                        hasPreviousVersion
                                    ) {
                                        this.setState({
                                            showingModal:
                                                "confirmPublishNewVersion",
                                        });
                                    } else {
                                        this.handlePublishClick();
                                    }
                                }}
                            >
                                {currentState === "PUBLISHED" ||
                                hasPreviousVersion
                                    ? "Publish new version"
                                    : "Publish"}
                            </Button>
                        )}
                    {this.props.activeSubscription.questionAuditor &&
                        isLatestVersion &&
                        isNextStateAllowed("NEEDS_REVISION") && (
                            <Button
                                bsStyle={getStatusBsStyle("NEEDS_REVISION")}
                                onClick={(event) =>
                                    this.handleNeedsRevisionClick(event)
                                }
                            >
                                Needs Revision
                            </Button>
                        )}
                    {!this.props.isPreview &&
                        isAuthorOrAuditor &&
                        isNextStateAllowed("DELETED") && (
                            <Button
                                bsStyle={"danger"}
                                onClick={(event) =>
                                    this.handleConfirmDeleteClick(event)
                                }
                            >
                                Delete
                            </Button>
                        )}
                    {this.props.isPreview && (
                        <Button
                            type="button"
                            bsStyle="link"
                            onClick={() => {
                                this.props.hasChanges
                                    ? this.setState({
                                          showingModal: "confirmDiscard",
                                      })
                                    : // No changes, no need to cofirm, just leave
                                      this.discardChanges();
                            }}
                        >
                            {isNew
                                ? "Discard Draft"
                                : this.props.hasChanges
                                ? "Discard changes"
                                : "Cancel editing"}
                        </Button>
                    )}
                </div>
            </form>
        );

        const closeModal = () => this.setState({ showingModal: undefined });

        return (
            <>
                <div className="question">
                    <QuestionInfo question={question} />
                    <QuestionTabs
                        questionView={questionView}
                        commentsView={
                            <Comments
                                me={this.props.me}
                                // TODO: Fix?
                                questionCreatedByUuid={
                                    (question as any).createdByUuid
                                }
                                qBankApi={qBankApi}
                                questionId={question.uuid}
                                comments={this.state.commentList}
                                addComment={this.addComment}
                            />
                        }
                    />
                </div>
                {this.state.showingModal && (
                    <QuestionViewerModals
                        state={this.state.showingModal}
                        isNew={isNew}
                        isPublished={question.status === "PUBLISHED"}
                        hasPrevVersion={hasPreviousVersion}
                        onCancel={closeModal}
                        onConfirm={
                            this.state.showingModal === "confirmDelete"
                                ? this.handleDeleteClick
                                : this.state.showingModal === "confirmDiscard"
                                ? this.discardChanges
                                : this.state.showingModal ===
                                  "saveAsNewVersionDraft"
                                ? this.handleSaveAsDraftClick
                                : this.state.showingModal ===
                                  "saveSubmittedAsDraft"
                                ? this.handleSaveAsDraftClick
                                : this.state.showingModal ===
                                  "confirmSubmitNewVersion"
                                ? this.handleSubmitClick
                                : this.state.showingModal ===
                                  "confirmPublishNewVersion"
                                ? this.handlePublishClick
                                : // If we've handled all cases, this type is never, otherwise this'll be a type error
                                  this.state.showingModal
                        }
                    />
                )}
            </>
        );
    }
}
export const QuestionViewer = withRouter(_QuestionViewer);

// Standalone question viewer, loads the question from the API
type QuestionViewerLoadState =
    | { state: "loading" }
    | {
          state: "loaded";
          question: QuestionViewerQuestionData;
      };

const QuestionViewerWrapper = (props: QuestionViewWrapperProps) => {
    const [state, setState] = useState<QuestionViewerLoadState>({
        state: "loading",
    });
    const { questionId } = useParams();
    assert(questionId, "Expected to have a questionId");
    const history = useHistory();
    useEffect(() => {
        let questionBodyPromise = questionApi.getQuestion(questionId);
        let questionImagesPromise = qBankApi.getQuestionImages(questionId);

        Promise.all([questionBodyPromise, questionImagesPromise])
            .then(([question, images]) => {
                const [
                    questionStemImages,
                    questionExplanationImages,
                ] = partitionImages(images);
                setState({
                    state: "loaded",
                    question: {
                        ...question,
                        questionStemImages,
                        questionExplanationImages,
                        removedImages: [],
                    },
                });
            })
            .catch((err) => {
                console.log("Error viewing question", err);
                history.push("/questions");
            });
    }, [history, questionId]);
    if (state.state === "loading") {
        return (
            <div className="loading">
                <span>Loading</span>
            </div>
        );
    }
    return (
        <QuestionViewer
            {...props}
            question={state.question}
            isPreview={false}
            hasChanges={false} // Only applies to preview
            onEdit={() => history.push(`/questions/${questionId}/edit`)}
        />
    );
};

export default QuestionViewerWrapper;
